import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { AmberInterval, HistorySample, Weather } from '../api';

// Wheel / pinch zoom bottoms out around a half-hour window — tighter than
// that and 5-minute samples become three lonely dots.
const MIN_SPAN_MS = 30 * 60_000;
const ZOOM_IN = 0.8;
const ZOOM_OUT = 1.25;
const DRAG_PX = 6;

type View = { t0: number; t1: number };
type XY = { t: number; v: number };

type FcPrice = XY & {
  descriptor?: string | null;
  end: number;
};

type Readout = {
  t: number;
  forecast: boolean;
  waterTempC: number | null;
  ambientC: number | null;
  amberPerKwh: number | null;
  amberDescriptor: string | null;
  heater: string | null;
};

// Amber quotes retail cents per kWh, GST in — unlike wholesale RRP ($/MWh).
const fmtCents = (c: number | null | undefined) =>
  c == null ? '—' : `${c.toFixed(1)} c/kWh`;

const priceOf = (p: HistorySample) => p.amberPerKwh;

function parseTime(ts: string | undefined) {
  if (!ts) return null;
  const t = new Date(ts).getTime();
  return Number.isFinite(t) ? t : null;
}

function clampView(t0: number, t1: number, full0: number, full1: number): View {
  const fullSpan = Math.max(1, full1 - full0);
  const minSpan = Math.min(fullSpan, MIN_SPAN_MS);
  const span = Math.min(fullSpan, Math.max(minSpan, t1 - t0));
  if (span >= fullSpan * 0.995) return { t0: full0, t1: full1 };
  const start = Math.max(full0, Math.min(t0, full1 - span));
  return { t0: start, t1: start + span };
}

function isFullView(v: View, full0: number, full1: number) {
  return v.t0 <= full0 + 1 && v.t1 >= full1 - 1;
}

function nearestIndex(times: number[], t: number) {
  if (times.length === 0) return 0;
  let lo = 0;
  let hi = times.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(times[lo - 1] - t) <= Math.abs(times[lo] - t)) return lo - 1;
  return lo;
}

function fmtTick(t: number, span: number) {
  const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', hour12: true };
  if (span < 4 * 3600_000) opts.minute = '2-digit';
  // History + forecast routinely crosses midnight, so the weekday is what
  // stops "3 pm" meaning yesterday and tomorrow at once.
  if (span >= 16 * 3600_000) opts.weekday = 'short';
  return new Date(t).toLocaleString([], opts).toLowerCase();
}

function fmtWhen(ts: number) {
  return new Date(ts).toLocaleString([], {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function windowSlice(times: number[], t0: number, t1: number) {
  let start = 0;
  while (start < times.length - 1 && times[start + 1] < t0) start += 1;
  let end = times.length - 1;
  while (end > 0 && times[end - 1] > t1) end -= 1;
  return { start, end };
}

function sliceByTime<T extends { t: number }>(pts: T[], t0: number, t1: number): T[] {
  if (!pts.length) return [];
  const { start, end } = windowSlice(pts.map((p) => p.t), t0, t1);
  return pts.slice(start, end + 1);
}

function holdTo(pts: XY[], t: number): XY[] {
  if (!pts.length) return pts;
  const last = pts[pts.length - 1];
  if (last.t < t) return [...pts, { t, v: last.v }];
  return pts;
}

function uniqueTimes(values: number[]) {
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [];
  for (const t of sorted) {
    if (!out.length || t - out[out.length - 1] >= 1000) out.push(t);
  }
  return out;
}

type Gesture =
  | { kind: 'pan'; startX: number; view: View; dragged: boolean }
  | { kind: 'pinch'; startDist: number; startSpan: number; anchor: number };

function seriesFromSamples(
  rows: { t: number; s: HistorySample }[],
  get: (s: HistorySample) => number | null | undefined,
  until: number
): XY[] {
  const out: XY[] = [];
  for (const { t, s } of rows) {
    if (t > until) break;
    const v = get(s);
    if (v == null) continue;
    out.push({ t, v });
  }
  return out;
}

function nearestXY(pts: XY[], t: number, maxDist: number): XY | null {
  if (!pts.length) return null;
  const i = nearestIndex(pts.map((p) => p.t), t);
  if (Math.abs(pts[i].t - t) > maxDist) return null;
  return pts[i];
}

function amberAt(intervals: FcPrice[], t: number): FcPrice | null {
  for (const iv of intervals) {
    if (t >= iv.t && t < iv.end) return iv;
  }
  return nearestXY(intervals, t, 20 * 60_000) as FcPrice | null;
}

// Interactive history-plus-forecast. Still hand-rolled — three series on a
// shared time axis is not worth a chart library, and zoom/inspect is a few
// pointers on the same SVG. Recorded data sits left of "now" at full opacity;
// forecast continues to the right, dashed and on a shaded band, so the split
// is in both form and region. Price stays a filled area on the right axis
// while it is recorded, then a dashed line once it is a forecast.
export function Chart({
  samples,
  amberForecast,
  weatherForecast,
}: {
  samples: HistorySample[];
  amberForecast?: AmberInterval[];
  weatherForecast?: Weather['forecast'];
}) {
  const now = useMemo(
    () => Date.now(),
    [samples, amberForecast, weatherForecast]
  );

  const hist = useMemo(
    () =>
      samples
        .map((s) => ({ s, t: parseTime(s.ts) }))
        .filter((row): row is { s: HistorySample; t: number } => row.t != null)
        .filter(({ s, t }) => t <= now && (s.waterTempC != null || s.ambientC != null || s.amberPerKwh != null)),
    [samples, now]
  );

  const priceFc = useMemo(() => {
    const out: FcPrice[] = [];
    for (const iv of amberForecast ?? []) {
      if (iv.perKwh == null) continue;
      const t = parseTime(iv.startTime);
      if (t == null || t < now - 60_000) continue;
      const end = parseTime(iv.endTime) ?? t + 30 * 60_000;
      out.push({ t: Math.max(t, now), v: iv.perKwh, descriptor: iv.descriptor ?? null, end });
    }
    out.sort((a, b) => a.t - b.t);
    const last = out.at(-1);
    if (last && last.end > last.t) {
      out.push({ t: last.end, v: last.v, descriptor: last.descriptor, end: last.end });
    }
    return out;
  }, [amberForecast, now]);

  const tempFc = useMemo(() => {
    const out: XY[] = [];
    for (const hour of weatherForecast ?? []) {
      if (hour.tempC == null) continue;
      const t = parseTime(hour.time);
      if (t == null || t <= now) continue;
      out.push({ t, v: hour.tempC });
    }
    out.sort((a, b) => a.t - b.t);
    return out;
  }, [weatherForecast, now]);

  const waterHist = useMemo(
    () => holdTo(seriesFromSamples(hist, (s) => s.waterTempC, now), now),
    [hist, now]
  );
  const ambientHist = useMemo(
    () => holdTo(seriesFromSamples(hist, (s) => s.ambientC, now), now),
    [hist, now]
  );
  const priceHist = useMemo(
    () => holdTo(seriesFromSamples(hist, priceOf, now), now),
    [hist, now]
  );

  const ambientFcLine = useMemo(() => {
    if (!tempFc.length) return [] as XY[];
    const last = ambientHist.at(-1);
    const bridge = last ? [{ t: now, v: last.v }] : [];
    if (bridge.length && tempFc[0].t - now < 2000) return tempFc;
    return [...bridge, ...tempFc];
  }, [ambientHist, tempFc, now]);

  const priceFcLine = useMemo(() => {
    if (!priceFc.length) return [] as XY[];
    const last = priceHist.at(-1);
    const bridge = last ? [{ t: now, v: last.v }] : [];
    if (bridge.length && priceFc[0].t - now < 2000) return priceFc;
    return [...bridge, ...priceFc];
  }, [priceHist, priceFc, now]);

  const times = useMemo(
    () =>
      uniqueTimes([
        ...hist.map((row) => row.t),
        ...priceFc.map((p) => p.t),
        ...tempFc.map((p) => p.t),
        ...(priceFc.length || tempFc.length ? [now] : []),
      ]),
    [hist, priceFc, tempFc, now]
  );

  const full0 = times.length ? Math.min(...times) : now;
  const full1 = times.length ? Math.max(...times) : now + 1;

  const [view, setView] = useState<View | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [pinnedIdx, setPinnedIdx] = useState<number | null>(null);
  const [panning, setPanning] = useState(false);

  const wrapRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const pointers = useRef(new Map<number, number>());
  const viewRef = useRef<View | null>(view);
  viewRef.current = view;

  const reactId = useId().replace(/:/g, '');
  const fillId = `priceFill-${reactId}`;
  const fillFcId = `priceFillFc-${reactId}`;
  const clipId = `plotClip-${reactId}`;

  const W = 720;
  const H = 240;
  const PAD = { l: 38, r: 52, t: 22, b: 26 };
  const innerW = W - PAD.l - PAD.r;

  const vis = view ?? { t0: full0, t1: full1 };
  const visSpan = Math.max(1, vis.t1 - vis.t0);
  const zoomed = view != null && !isFullView(view, full0, full1);

  const histDrawn = sliceByTime(hist, vis.t0, vis.t1);
  const waterDrawn = sliceByTime(waterHist, vis.t0, vis.t1);
  const ambientDrawn = sliceByTime(ambientHist, vis.t0, vis.t1);
  const priceDrawn = sliceByTime(priceHist, vis.t0, vis.t1);
  const ambientFcDrawn = sliceByTime(ambientFcLine, vis.t0, vis.t1);
  const priceFcDrawn = sliceByTime(priceFcLine, vis.t0, vis.t1);

  const temps = [
    ...waterDrawn.map((p) => p.v),
    ...ambientDrawn.map((p) => p.v),
    ...ambientFcDrawn.map((p) => p.v),
  ];
  // When the window is empty (mid-pan edge), fall back to the full series so
  // the axes do not collapse.
  if (!temps.length) {
    temps.push(
      ...waterHist.map((p) => p.v),
      ...ambientHist.map((p) => p.v),
      ...ambientFcLine.map((p) => p.v)
    );
  }
  const tMinRaw = temps.length ? Math.min(...temps) : 0;
  const tMaxRaw = temps.length ? Math.max(...temps) : 40;
  const tStep = !zoomed || tMaxRaw - tMinRaw > 8 ? 5 : 1;
  const tFloor = zoomed ? tMinRaw : Math.min(tMinRaw, 0);
  const tCeil = zoomed ? tMaxRaw : Math.max(tMaxRaw, 40);
  let tMin = Math.floor(tFloor / tStep) * tStep;
  let tMax = Math.ceil(tCeil / tStep) * tStep;
  if (tMax <= tMin) {
    tMin -= tStep;
    tMax += tStep;
  }

  const prices = [
    ...priceDrawn.map((p) => p.v),
    ...priceFcDrawn.map((p) => p.v),
  ];
  if (!prices.length) {
    prices.push(...priceHist.map((p) => p.v), ...priceFcLine.map((p) => p.v));
  }
  // Price can go negative — it genuinely does — so the unzoomed axis starts at
  // the minimum, not at zero, or those intervals vanish off the bottom. Zoomed
  // in, use the visible range so a cheap window is actually readable.
  const pMin = prices.length ? Math.min(...prices, zoomed ? prices[0] : 0) : 0;
  const pMax = prices.length ? Math.max(...prices, zoomed ? prices[0] : 1) : 1;
  const pSpan = Math.max(1, pMax - pMin);
  // Price is confined to the bottom 55% of the plot. Given the full height it
  // fills the chart and pulls the eye off the temperatures, which are the
  // subject — price is context for why the water is doing what it is doing.
  const PRICE_BAND = 0.55;

  const x = (t: number) => PAD.l + ((t - vis.t0) / visSpan) * innerW;
  const y = (v: number) =>
    H - PAD.b - ((v - tMin) / Math.max(1, tMax - tMin)) * (H - PAD.t - PAD.b);
  const yP = (v: number) =>
    H - PAD.b - ((v - pMin) / pSpan) * (H - PAD.t - PAD.b) * PRICE_BAND;

  const pathXY = (pts: XY[], scale: (v: number) => number) => {
    let d = '';
    let pen = false;
    for (const p of pts) {
      d += `${pen ? 'L' : 'M'}${x(p.t).toFixed(1)},${scale(p.v).toFixed(1)}`;
      pen = true;
    }
    return d;
  };

  // Historical samples still lift the pen on missing readings so a dropped
  // sensor does not draw a misleading straight line across the gap.
  const pathSamples = (
    get: (p: HistorySample) => number | null | undefined,
    scale: (v: number) => number
  ) => {
    let d = '';
    let pen = false;
    for (const { s, t } of histDrawn) {
      const v = get(s);
      if (v == null) {
        pen = false;
        continue;
      }
      d += `${pen ? 'L' : 'M'}${x(t).toFixed(1)},${scale(v).toFixed(1)}`;
      pen = true;
    }
    return d;
  };

  const priceArea = (pts: XY[]) => {
    const line = pathXY(pts, yP);
    if (!line) return '';
    const first = pts[0];
    const last = pts[pts.length - 1];
    const base = H - PAD.b;
    return `${line}L${x(last.t).toFixed(1)},${base}L${x(first.t).toFixed(1)},${base}Z`;
  };

  const geomRef = useRef({ W, innerW, PAD, full0, full1, times });
  geomRef.current = { W, innerW, PAD, full0, full1, times };

  const applyView = (next: View) => {
    const { full0: f0, full1: f1 } = geomRef.current;
    const clamped = clampView(next.t0, next.t1, f0, f1);
    setView(isFullView(clamped, f0, f1) ? null : clamped);
  };

  const timeFromClientX = (clientX: number) => {
    const el = wrapRef.current;
    const { W: w, innerW: iw, PAD: pad, full0: f0, full1: f1 } = geomRef.current;
    const current = viewRef.current ?? { t0: f0, t1: f1 };
    if (!el) return (current.t0 + current.t1) / 2;
    const rect = el.getBoundingClientRect();
    const svgX = ((clientX - rect.left) / Math.max(1, rect.width)) * w;
    const frac = (svgX - pad.l) / Math.max(1, iw);
    return current.t0 + frac * (current.t1 - current.t0);
  };

  const zoomAt = (factor: number, anchor: number) => {
    const { full0: f0, full1: f1 } = geomRef.current;
    const current = viewRef.current ?? { t0: f0, t1: f1 };
    const span = current.t1 - current.t0;
    const frac = (anchor - current.t0) / Math.max(1, span);
    const nextSpan = span * factor;
    applyView({ t0: anchor - frac * nextSpan, t1: anchor - frac * nextSpan + nextSpan });
  };

  const idxAtClientX = (clientX: number) => {
    const { times: ts } = geomRef.current;
    if (!ts.length) return null;
    return nearestIndex(ts, timeFromClientX(clientX));
  };

  useEffect(() => {
    if (!times.length) return;
    setView((prev) => {
      if (!prev) return null;
      const next = clampView(prev.t0, prev.t1, full0, full1);
      return isFullView(next, full0, full1) ? null : next;
    });
    setPinnedIdx((idx) => (idx != null && idx < times.length ? idx : null));
  }, [full0, full1, times.length]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { full0: f0, full1: f1 } = geomRef.current;
      const current = viewRef.current ?? { t0: f0, t1: f1 };
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && !isFullView(current, f0, f1)) {
        const shift = (e.deltaX / Math.max(1, el.clientWidth)) * (current.t1 - current.t0);
        applyView({ t0: current.t0 + shift, t1: current.t1 + shift });
        return;
      }
      zoomAt(e.deltaY > 0 ? ZOOM_OUT : ZOOM_IN, timeFromClientX(e.clientX));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [times.length]);

  const readoutAt = (t: number): Readout => {
    const forecast = t > now;
    if (!forecast) {
      if (!hist.length) {
        return {
          t, forecast: false,
          waterTempC: null, ambientC: null, amberPerKwh: null,
          amberDescriptor: null, heater: null,
        };
      }
      const i = nearestIndex(hist.map((row) => row.t), t);
      const s = hist[i].s;
      return {
        t: hist[i].t,
        forecast: false,
        waterTempC: s.waterTempC ?? null,
        ambientC: s.ambientC ?? null,
        amberPerKwh: priceOf(s) ?? null,
        amberDescriptor: s.amberDescriptor ?? null,
        heater: s.heater ?? null,
      };
    }
    const ambient = nearestXY(tempFc, t, 50 * 60_000);
    const price = amberAt(priceFc, t);
    return {
      t,
      forecast: true,
      waterTempC: null,
      ambientC: ambient?.v ?? null,
      amberPerKwh: price?.v ?? null,
      amberDescriptor: price?.descriptor ?? null,
      heater: null,
    };
  };

  const activeIdx = hoverIdx ?? pinnedIdx;
  const activeTime = activeIdx != null ? times[activeIdx] : undefined;
  const active = activeTime != null ? readoutAt(activeTime) : undefined;

  const hasForecast = priceFc.length > 0 || tempFc.length > 0;
  const nowInView = now >= vis.t0 && now <= vis.t1;
  const nowX = x(now);
  const fcBandLeft = nowInView ? nowX : now < vis.t0 ? PAD.l : null;
  const fcBandWidth =
    fcBandLeft == null ? 0 : Math.max(0, Math.min(W - PAD.r, x(vis.t1)) - fcBandLeft);

  if (hist.length < 2 && !hasForecast) {
    return <p className="sub">Not enough history yet — samples are recorded every 5 minutes.</p>;
  }

  function onPointerDown(e: React.PointerEvent<SVGSVGElement>) {
    if (e.button !== 0) return;
    wrapRef.current?.focus({ preventScroll: true });
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, e.clientX);
    const { full0: f0, full1: f1 } = geomRef.current;
    const current = viewRef.current ?? { t0: f0, t1: f1 };

    if (pointers.current.size === 2) {
      const xs = [...pointers.current.values()];
      const dist = Math.abs(xs[0] - xs[1]);
      const mid = (xs[0] + xs[1]) / 2;
      gesture.current = {
        kind: 'pinch',
        startDist: Math.max(8, dist),
        startSpan: current.t1 - current.t0,
        anchor: timeFromClientX(mid),
      };
      setPanning(true);
      return;
    }

    gesture.current = { kind: 'pan', startX: e.clientX, view: current, dragged: false };
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, e.clientX);
    const g = gesture.current;
    const { full0: f0, full1: f1, times: ts } = geomRef.current;

    if (g?.kind === 'pinch' && pointers.current.size === 2) {
      const xs = [...pointers.current.values()];
      const dist = Math.max(8, Math.abs(xs[0] - xs[1]));
      const factor = g.startDist / dist;
      const nextSpan = g.startSpan * factor;
      const current = viewRef.current ?? { t0: f0, t1: f1 };
      const frac = (g.anchor - current.t0) / Math.max(1, current.t1 - current.t0);
      applyView({ t0: g.anchor - frac * nextSpan, t1: g.anchor - frac * nextSpan + nextSpan });
      return;
    }

    if (g?.kind === 'pan') {
      const dx = e.clientX - g.startX;
      if (!g.dragged && Math.abs(dx) >= DRAG_PX) {
        g.dragged = true;
        setPanning(true);
      }
      if (g.dragged && !isFullView(g.view, f0, f1)) {
        const el = wrapRef.current;
        const shift = (-dx / Math.max(1, el?.clientWidth ?? 1)) * (g.view.t1 - g.view.t0);
        applyView({ t0: g.view.t0 + shift, t1: g.view.t1 + shift });
      }
      if (g.dragged) return;
    }

    if (ts.length) setHoverIdx(nearestIndex(ts, timeFromClientX(e.clientX)));
  }

  function onPointerUp(e: React.PointerEvent<SVGSVGElement>) {
    const g = gesture.current;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2 && g?.kind === 'pinch') gesture.current = null;
    if (pointers.current.size === 0) {
      if (g?.kind === 'pan' && !g.dragged) {
        const idx = idxAtClientX(e.clientX);
        setPinnedIdx((prev) => (idx != null && prev === idx ? null : idx));
        setHoverIdx(idx);
      }
      gesture.current = null;
      setPanning(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const { full0: f0, full1: f1, times: ts } = geomRef.current;
    const current = viewRef.current ?? { t0: f0, t1: f1 };
    if (e.key === 'Escape') {
      if (pinnedIdx != null) {
        setPinnedIdx(null);
        return;
      }
      if (zoomed) setView(null);
      return;
    }
    if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      const anchor = activeTime ?? (current.t0 + current.t1) / 2;
      zoomAt(ZOOM_OUT, anchor);
      return;
    }
    if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      const anchor = activeTime ?? (current.t0 + current.t1) / 2;
      zoomAt(ZOOM_IN, anchor);
      return;
    }
    if (e.key === '0') {
      setView(null);
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      if (!ts.length) return;
      const from = activeIdx ?? nearestIndex(ts, (current.t0 + current.t1) / 2);
      const next = Math.max(0, Math.min(ts.length - 1, from + (e.key === 'ArrowRight' ? 1 : -1)));
      setPinnedIdx(next);
      setHoverIdx(next);
      if (ts[next] < current.t0 || ts[next] > current.t1) {
        const span = current.t1 - current.t0;
        applyView({ t0: ts[next] - span / 2, t1: ts[next] + span / 2 });
      }
    }
  }

  const ticks = [0, 1, 2, 3].map((i) => vis.t0 + (i / 3) * visSpan);
  const tempTicks = [tMin, (tMin + tMax) / 2, tMax];
  const zoomAnchor = activeTime ?? (vis.t0 + vis.t1) / 2;
  const showRecLabel = nowInView && nowX > PAD.l + 52;
  const showFcLabel = nowInView && nowX < W - PAD.r - 52;

  return (
    <>
      <div className="chart-toolbar">
        <p className="hint">
          {zoomed
            ? 'Drag to pan · scroll or pinch to zoom'
            : hasForecast
              ? 'Left of Now is recorded · shaded band is forecast · scroll to zoom'
              : 'Scroll or pinch to zoom · tap a point for values'}
        </p>
        <div className="chart-zoom">
          <button type="button" className="action" onClick={() => zoomAt(ZOOM_OUT, zoomAnchor)}
                  aria-label="Zoom out">−</button>
          <button type="button" className="action" onClick={() => zoomAt(ZOOM_IN, zoomAnchor)}
                  aria-label="Zoom in">+</button>
          <button type="button" className="action" disabled={!zoomed} onClick={() => setView(null)}>
            Reset
          </button>
        </div>
      </div>

      <div
        ref={wrapRef}
        className="chart-frame"
        tabIndex={0}
        role="region"
        aria-label="Water temperature, ambient temperature and Amber electricity price, showing recorded history and forecast. Scroll to zoom, click a point to inspect."
        onKeyDown={onKeyDown}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <svg
          className={`chart${panning ? ' is-panning' : ''}`}
          viewBox={`0 0 ${W} ${H}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={() => setView(null)}
        >
          <defs>
            <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--price)" stopOpacity="0.24" />
              <stop offset="100%" stopColor="var(--price)" stopOpacity="0.03" />
            </linearGradient>
            <linearGradient id={fillFcId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--price)" stopOpacity="0.12" />
              <stop offset="100%" stopColor="var(--price)" stopOpacity="0.02" />
            </linearGradient>
            <clipPath id={clipId}>
              <rect x={PAD.l} y={PAD.t} width={innerW} height={H - PAD.t - PAD.b} />
            </clipPath>
          </defs>

          {tempTicks.map((v) => (
            <g key={v}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="var(--line)" />
              <text x={PAD.l - 6} y={y(v) + 4} fill="var(--muted)" fontSize="10" textAnchor="end">
                {v}°
              </text>
            </g>
          ))}

          {ticks.map((t, i) => (
            <text key={`${t}-${i}`} x={x(t)} y={H - 8} fill="var(--muted)" fontSize="10"
                  textAnchor={i === 0 ? 'start' : i === 3 ? 'end' : 'middle'}>
              {fmtTick(t, visSpan)}
            </text>
          ))}

          {showRecLabel && (
            <text x={nowX - 8} y={14} fill="var(--muted)" fontSize="10" textAnchor="end">
              Recorded
            </text>
          )}
          {showFcLabel && (
            <text x={nowX + 8} y={14} fill="var(--accent)" fontSize="10" textAnchor="start">
              Forecast
            </text>
          )}

          <g clipPath={`url(#${clipId})`}>
            {fcBandLeft != null && fcBandWidth > 0 && (
              <rect
                x={fcBandLeft} y={PAD.t} width={fcBandWidth} height={H - PAD.t - PAD.b}
                fill="var(--accent)" opacity="0.1"
              />
            )}

            {priceDrawn.length > 0 && (
              <>
                <path d={priceArea(priceDrawn)} fill={`url(#${fillId})`} stroke="none" />
                <path d={pathXY(priceDrawn, yP)} fill="none" stroke="var(--price)"
                      strokeWidth="1.25" opacity="0.9" />
              </>
            )}
            {priceFcDrawn.length > 0 && (
              <>
                <path d={priceArea(priceFcDrawn)} fill={`url(#${fillFcId})`} stroke="none" />
                <path d={pathXY(priceFcDrawn, yP)} fill="none" stroke="var(--price)"
                      strokeWidth="1.25" strokeDasharray="6 4" opacity="0.75" />
              </>
            )}
            {ambientDrawn.length > 0 && (
              <path d={pathXY(ambientDrawn, y)} fill="none" stroke="var(--cold)"
                    strokeWidth="1.5" strokeDasharray="4 3" strokeLinejoin="round" />
            )}
            {ambientFcDrawn.length > 0 && (
              <path d={pathXY(ambientFcDrawn, y)} fill="none" stroke="var(--cold)"
                    strokeWidth="1.5" strokeDasharray="1.5 3" strokeLinejoin="round" opacity="0.7" />
            )}
            <path d={pathSamples((p) => p.waterTempC, y)} fill="none" stroke="var(--hot)"
                  strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            {nowInView && waterHist.length >= 2 && (
              <path d={pathXY(waterHist.slice(-2), y)} fill="none" stroke="var(--hot)"
                    strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            )}

            {nowInView && (
              <line
                x1={nowX} x2={nowX} y1={PAD.t} y2={H - PAD.b}
                stroke="var(--accent)" strokeWidth="1.25" strokeDasharray="3 3"
              />
            )}

            {active && activeTime != null && (
              <line
                x1={x(activeTime)} x2={x(activeTime)} y1={PAD.t} y2={H - PAD.b}
                stroke={pinnedIdx != null && hoverIdx == null ? 'var(--accent)' : 'var(--text)'}
                strokeOpacity="0.35" strokeWidth="1"
              />
            )}
          </g>

          {prices.length > 0 && (
            <>
              <text x={W - PAD.r + 6} y={yP(pMax) + 4} fill="var(--price)" fontSize="10">
                {pMax.toFixed(0)}c
              </text>
              <text x={W - PAD.r + 6} y={yP(pMin) + 4} fill="var(--price)" fontSize="10">
                {pMin.toFixed(0)}c
              </text>
            </>
          )}

          {active && activeTime != null && (
            <>
              {active.ambientC != null && (
                <circle cx={x(activeTime)} cy={y(active.ambientC)} r="3.5"
                        fill="var(--bg)" stroke="var(--cold)" strokeWidth="1.5" />
              )}
              {active.amberPerKwh != null && (
                <circle cx={x(activeTime)} cy={yP(active.amberPerKwh)} r="3.5"
                        fill="var(--bg)" stroke="var(--price)" strokeWidth="1.5" />
              )}
              {active.waterTempC != null && (
                <circle cx={x(activeTime)} cy={y(active.waterTempC)} r="4.5"
                        fill="var(--hot)" stroke="var(--bg)" strokeWidth="1.5" />
              )}
            </>
          )}
        </svg>
      </div>

      {active && (
        <div className={`chart-readout${pinnedIdx != null && hoverIdx == null ? ' pinned' : ''}`}
             aria-live="polite">
          <span className="when">{fmtWhen(active.t)}</span>
          {hasForecast && (active.forecast
            ? <span className="tag">Forecast</span>
            : <span className="tag recorded">Recorded</span>)}
          <span className="kv water">Water <b>{active.waterTempC != null ? `${active.waterTempC.toFixed(2)}°` : '—'}</b></span>
          <span className="kv ambient">Ambient <b>{active.ambientC != null ? `${active.ambientC.toFixed(1)}°` : '—'}</b></span>
          <span className="kv price">Amber <b>{fmtCents(active.amberPerKwh)}</b></span>
          {active.amberDescriptor && (
            <span className="kv">{active.amberDescriptor.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}</span>
          )}
          {active.heater != null && (
            <span className="kv">Heater <b>{active.heater}</b></span>
          )}
          {pinnedIdx != null && hoverIdx == null && (
            <button type="button" className="link" onClick={() => setPinnedIdx(null)}>Clear</button>
          )}
        </div>
      )}

      <div className="legend">
        <span><i className="sw solid" style={{ background: 'var(--hot)' }} />Water temp</span>
        <span><i className="sw dash" style={{ borderColor: 'var(--cold)' }} />
          Ambient {hasForecast ? 'recorded' : 'temp'}</span>
        {tempFc.length > 0 && (
          <span><i className="sw dot" style={{ borderColor: 'var(--cold)' }} />Ambient forecast</span>
        )}
        <span><i className="sw area" style={{ background: 'var(--price)' }} />
          Amber {hasForecast ? 'recorded' : 'price'} ({fmtCents(pMin)}–{fmtCents(pMax)}, right axis)</span>
        {priceFc.length > 0 && (
          <span><i className="sw dash" style={{ borderColor: 'var(--price)' }} />Amber forecast</span>
        )}
        {hasForecast && (
          <span><i className="sw now" />Now</span>
        )}
      </div>
    </>
  );
}
